import { Store, StoreSyncInfo, StoreRSInfo, NewStoreInfo, StoreCSVInfo, SyncFilters, LogEntry, LogFilters, LogResponse, LogsPage, ProductReportPage, ProductReportDetail, CategoryReportPage, CategoryReportDetail, BrandReportPage, BrandReportDetail, ProductRangeReportPage, ProductRangeReportDetail } from "../types";
import accountsData from './accounts.json';
import { SyncStoreRequest } from "./dtos/storeSyncRequest";

// Only required logs throw, so the caller can stop a destructive action when logging fails
export const createLog = async (logData: LogEntry, required = false) => {
    try {
        const res = await fetch(`${process.env.REACT_APP_API_NODE_URL}logs`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify(logData)
        });

        if (!res.ok) throw new Error(`Failed to create log (HTTP ${res.status})`);
    } catch (error) {
        if (required) throw error;
        console.error("Error creating log:", error);
    }
}

// Fetches global logs when no storeId is provided
export const getLogs = async (storeId: number | undefined, { entityType, section, userEmail, searchIn, from, to, limit, page }: LogFilters = {}): Promise<LogsPage> => {
    try {
        const query = new URLSearchParams();
        if (entityType) query.append('entityType', entityType);
        if (section) query.append('section', section);
        if (userEmail) query.append('userEmail', userEmail);
        if (userEmail && searchIn) query.append('searchIn', searchIn);
        if (from) query.append('from', from);
        if (to) query.append('to', to);
        if (limit) query.append('limit', limit.toString());
        if (page) query.append('page', page.toString());

        const res = await fetch(`${process.env.REACT_APP_API_NODE_URL}logs/${storeId ?? 'global'}?${query.toString()}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            },
            credentials: 'include'
        });

        if (!res.ok) {
            console.error("Failed to fetch logs:", res.statusText);
            return { items: [], total: 0 };
        }

        const data = await res.json();

        return {
            items: Array.isArray(data?.items) ? data.items : [],
            total: Number(data?.total ?? 0),
        };
    } catch (error) {
        console.error("Error fetching logs:", error);
        return { items: [], total: 0 };
    }
}

export const getLogEntry = async (logId: string): Promise<LogResponse | null> => {
    try {
        const res = await fetch(`${process.env.REACT_APP_API_NODE_URL}logs/entry/${logId}`, {
            method: 'GET',
            credentials: 'include'
        });

        if (!res.ok) {
            console.error("Failed to fetch log entry:", res.statusText);
            return null;
        }

        return await res.json();
    } catch (error) {
        console.error("Error fetching log entry:", error);
        return null;
    }
}

const urlStore = process.env.REACT_APP_API_NODE_URL + "stores";
const urlReport = process.env.REACT_APP_API_BASE_URL + "product/report/";
const urlCategories = process.env.REACT_APP_API_BASE_URL + "categories/report/";
const urlRanges = process.env.REACT_APP_API_BASE_URL + "productrange/report/";
const urlProductCategories = process.env.REACT_APP_API_BASE_URL + "productcategories/report/";
const urlBrands = process.env.REACT_APP_API_BASE_URL + "brand/report/";
const urlSignIn = process.env.REACT_APP_API_NODE_URL + "auth/signin";
const urlSync = process.env.REACT_APP_API_NODE_URL + "store";
const urlTimezone = process.env.REACT_APP_API_NODE_URL + "auth/timezone";
const urlGetJSONSettings = process.env.REACT_APP_API_BASE_URL + "ws/Customization/GetMetafield/Settings";
const urlPutJSONSettings = process.env.REACT_APP_API_BASE_URL + "ws/Customization/UpsertMetafield/Settings";

export async function getUsers(params: any): Promise<any> {
    const querySearch = new URLSearchParams();
    if (params.search) querySearch.set('search', params.search);
    if (params.scope) querySearch.set('scope', params.scope);
    if (params.status) querySearch.set('status', params.status);
    if (params.sort) querySearch.set('sort', params.sort);
    querySearch.set('limit', String(params.limit ?? 20));
    querySearch.set('page', String(params.page ?? 1));

    const url = `${process.env.REACT_APP_API_NODE_URL}users${querySearch.toString() ? `?${querySearch.toString()}` : ''}`;
    const res = await fetch(url, { credentials: 'include' });
    if (!res.ok) throw new Error('Failed to fetch users');
    const data = await res.json();
    return {
        items: Array.isArray(data.items) ? data.items : [],
        total: Number(data.total ?? 0),
        page: Number(data.page ?? 1),
        pageSize: Number(data.pageSize ?? (params.limit ?? 20)),
    };
}

export async function createUser(payload: any): Promise<any> {
    const url = `${process.env.REACT_APP_API_NODE_URL}user/create`;

    const res = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            email: payload.email.trim().toLowerCase(),
            scopes: Array.isArray(payload.scopes) ? payload.scopes : [],
            storeId: Number(payload.stores[0] || 0),
        }),
    });

    let data: any = null;
    try {
        data = await res.json();
    } catch {
    }

    if (!res.ok) {
        const msg = data?.error || "Failed to create user";
        throw new Error(msg);
    }

    return data;
}

export async function updateUser(userId: string, payload: any): Promise<any> {
    const url = `${process.env.REACT_APP_API_NODE_URL}users/${userId}`;

    const body = {} as any;
    if (typeof payload.email !== "undefined") body.email = String(payload.email).trim().toLowerCase();
    if (typeof payload.scopes !== "undefined") body.scopes = Array.isArray(payload.scopes) ? payload.scopes : [];
    if (typeof payload.isActive !== "undefined") body.isActive = !!payload.isActive;

    const storeId =
        Array.isArray(payload.stores) ? payload.stores[0] : payload.storeId ?? '';
    if (typeof storeId !== 'undefined') body.storeId = storeId ?? '';

    const res = await fetch(`${url}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.error || "Failed to update user");
    return data;
}

export async function updateUserStatus(userId: string, isActive: boolean, email: string): Promise<any> {
    const url = `${process.env.REACT_APP_API_NODE_URL}users/${userId}/status`;

    const res = await fetch(`${url}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ isActive: !!isActive, email }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.error || "Failed to update status");
    return data;
}

export async function sendInvite(userId: string): Promise<any> {
    const url = `${process.env.REACT_APP_API_NODE_URL}users/${userId}/invite`;

    const res = await fetch(`${url}`, {
        method: "POST",
        credentials: "include",
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.error || "Failed to send invite");
    return data;
}

export const getAllStores = async () => {
    const res = await fetch(urlStore, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json'
        },
        credentials: 'include'
    }).then(function (response) {
        return response.json();
    }).then(function (data) {
        return data;
    });
    return res;
}


export const getProductReport = async (storeId: number, page: number, pageSize: number, search: string, status: string): Promise<ProductReportPage> => {
    const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
    if (search) params.set('search', search);
    if (status) params.set('status', status);

    const res = await fetch(`${urlReport}${storeId}?${params}`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json'
        }
    });

    if (!res.ok) throw new Error(`Failed to retrieve the product report (HTTP ${res.status})`);

    return await res.json();
}

export const getProductDetail = async (storeId: number, productId: number): Promise<ProductReportDetail> => {
    const res = await fetch(`${urlReport}${storeId}/${productId}`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json'
        }
    });

    if (!res.ok) throw new Error(`Failed to retrieve the product detail (HTTP ${res.status})`);

    return await res.json();
}

export const getProductReportCsv = async (storeId: number, search: string, status: string, includeDetails: boolean): Promise<string> => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (status) params.set('status', status);
    if (includeDetails) params.set('details', 'true');

    const res = await fetch(`${urlReport}${storeId}/export?${params}`, {
        method: 'GET'
    });

    if (!res.ok) throw new Error(`Failed to export the product report (HTTP ${res.status})`);

    return await res.text();
}

export const getCategoryReport = async (storeId: number, page: number, pageSize: number, search: string, status: string): Promise<CategoryReportPage> => {
    const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
    if (search) params.set('search', search);
    if (status) params.set('status', status);

    const res = await fetch(`${urlCategories}${storeId}?${params}`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json'
        }
    });

    if (!res.ok) throw new Error(`Failed to retrieve the category report (HTTP ${res.status})`);

    return await res.json();
}

export const getCategoryDetail = async (storeId: number, categoryId: number): Promise<CategoryReportDetail> => {
    const res = await fetch(`${urlCategories}${storeId}/${categoryId}`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json'
        }
    });

    if (!res.ok) throw new Error(`Failed to retrieve the category detail (HTTP ${res.status})`);

    return await res.json();
}

export const getBrandReport = async (storeId: number, page: number, pageSize: number, search: string, status: string): Promise<BrandReportPage> => {
    const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
    if (search) params.set('search', search);
    if (status) params.set('status', status);

    const res = await fetch(`${urlBrands}${storeId}?${params}`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json'
        }
    });

    if (!res.ok) throw new Error(`Failed to retrieve the brand report (HTTP ${res.status})`);

    return await res.json();
}

export const getBrandDetail = async (storeId: number, brandId: number): Promise<BrandReportDetail> => {
    const res = await fetch(`${urlBrands}${storeId}/${brandId}`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json'
        }
    });

    if (!res.ok) throw new Error(`Failed to retrieve the brand detail (HTTP ${res.status})`);

    return await res.json();
}

export const getProductRangeReport = async (storeId: number, page: number, pageSize: number, search: string, status: string): Promise<ProductRangeReportPage> => {
    const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
    if (search) params.set('search', search);
    if (status) params.set('status', status);

    const res = await fetch(`${urlRanges}${storeId}?${params}`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json'
        }
    });

    if (!res.ok) throw new Error(`Failed to retrieve the product range report (HTTP ${res.status})`);

    return await res.json();
}

export const getProductRangeDetail = async (storeId: number, productRangeId: number): Promise<ProductRangeReportDetail> => {
    const res = await fetch(`${urlRanges}${storeId}/${productRangeId}`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json'
        }
    });

    if (!res.ok) throw new Error(`Failed to retrieve the product range detail (HTTP ${res.status})`);

    return await res.json();
}

export const getAllProductCategories = async (storeId: number) => {
    const res = await fetch(`${urlProductCategories}${storeId}`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json'
        }
    });

    const data = await res.json();
    if (data.productCategoriesReportResults.length != 0)
        return data.productCategoriesReportResults;
    else
        return null;
}

export const getStoreById = async (storeId: number) => {
    if (storeId === 0) return null;
    const res = await fetch(`${urlStore}/${storeId}`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json'
        },
        credentials: 'include'
    }).then(function (response) {
        return response.json();
    }).then(function (data) {
        return data;
    });
    return res;
}

export const enableStore = async (storeId: number, enabled: boolean, reason?: string) => {
    const body = JSON.stringify({ enabled });
    const res = await fetch(`${urlStore}/update/enable/${storeId}`, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json'
        },
        credentials: 'include',
        body
    }).then(function (response) {
        return response.json();
    }).then(function (data) {
        return data;
    });

    if (res) {
        await createLog({
            storeId: storeId,
            entityType: 'sync',
            section: 'syncSettings',
            action: enabled ? 'enable' : 'disable',
            newValue: { enabled, reason },
            lastValue: { enabled: !enabled }
        });
    }

    return res;
}

export const syncCall = async (storeId: number, request: SyncStoreRequest = {}) => {
  const { email, ...body } = request;
  const query = email ? `?email=${encodeURIComponent(email)}` : '';
  const res = await fetch(`${urlSync}/SyncStore/${storeId}${query}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  });

  if (res.status === 200) {
    createLog({
      storeId,
      entityType: 'sync',
      section: 'manualSync',
      action: body.syncEntityModifier === 'Force' ? 'forceTrigger' : 'trigger',
      newValue: Object.keys(body).length > 0 ? body : { entity: 'all' },
    });
    return '';
  }

  const text = await res.text();
  if (!text) return `Sync failed (${res.status}).`;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
};

export const getStatus = async (storeId: number) => {
    const res = await fetch(`${urlSync}/status/${storeId}`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json'
        }
    }).then(function (response) {
        return response.json();
    }).then(function (data) {
        return data;
    });
    return res;
}

export const login = async () => {
    const res = await fetch(urlSignIn, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json'
        },
        credentials: 'include'
    });

    const data = await res.json();
    return data;
}

export const updateStoreSyncType = async (id: number, developmentPhase: number) => {
    const body = JSON.stringify({ developmentPhase });
    const response = await fetch(`${urlStore}/update/sync/${id}`, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json'
        },
        body,
        credentials: 'include'
    });

    if (!response.ok) {
        throw new Error(`Failed to edit store sync: ${response.statusText}`);
    }
    const data = await response.json();
    if (!data) {
        console.error('Received empty or invalid JSON response from the server.');
        throw new Error('Invalid server response.');
    }
    return data;
}

export const editStoreSync = async (storeSyncInfo: StoreSyncInfo) => {
    const body = JSON.stringify(storeSyncInfo);

    const response = await fetch(`${urlStore}/sync/${storeSyncInfo.id}`, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json'
        },
        body,
        credentials: 'include'
    });

    if (!response.ok) {
        throw new Error(`Failed to edit store sync: ${response.statusText}`);
    }
    const data = await response.json();
    if (!data) {
        console.error('Received empty or invalid JSON response from the server.');
        throw new Error('Invalid server response.');
    }
    return data;
}

export const editStoreRSInfo = async (updatedStore: StoreRSInfo) => {
    const body = JSON.stringify(updatedStore);
    const response = await fetch(`${urlStore}/update/${updatedStore.id}`, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json'
        },
        body,
        credentials: 'include'
    });
    if (!response.ok) {
        throw new Error(`Failed to edit store information: ${response.statusText}`);
    }
    const data = await response.json();
    if (!data) {
        console.error('Received empty or invalid JSON response from the server.');
        throw new Error('Invalid server response.');
    }
    return data;
}

// Same payload the RetailSystem form saves, with only the live status changed
export const takeStoreOffline = async (store: Store) => {
    const res = await editStoreRSInfo({
        id: store.id,
        friendlyName: store.friendlyName,
        accountGuid: store.accountGuid,
        accountId: store.accountId,
        dns: store.dns,
        storePlan: store.storePlan || 0,
        logo: store.logo,
        isMultiStore: store.isMultiStore,
        liveState: 0,
        orderDestinationAccountId: store.orderDestinationAccountId,
    });

    await createLog({
        storeId: store.id,
        entityType: 'storeSettings',
        section: 'retailSystem',
        action: 'save',
        newValue: { liveState: 0, via: 'decommission' },
        lastValue: { liveState: store.liveState }
    });

    return res;
}

export const editStoreCSVInfo = async (updatedStore: StoreCSVInfo) => {
    const body = JSON.stringify(updatedStore);
    const response = await fetch(`${urlStore}/update/csv/${updatedStore.id}`, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json'
        },
        body,
        credentials: 'include'
    });
    if (!response.ok) {
        throw new Error(`Failed to edit store information: ${response.statusText}`);
    }
    const data = await response.json();
    if (!data) {
        console.error('Received empty or invalid JSON response from the server.');
        throw new Error('Invalid server response.');
    }
    return data;
}

export const getCSVAccountData = async () => {
    try {
        return accountsData;
    } catch (error) {
        console.error('Error fetching JSON data:', error);
        return [];
    }
}

export const setNewTimezonePreference = async (preference: boolean) => {
    const body = JSON.stringify({ preference });
    const response = await fetch(`${urlTimezone}`, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json'
        },
        body,
        credentials: 'include'
    });
    if (!response.ok) {
        throw new Error(`Failed to edit store information: ${response.statusText}`);
    }
    const data = await response.json();
    if (!data) {
        console.error('Received empty or invalid JSON response from the server.');
        throw new Error('Invalid server response.');
    }
    return data;
}

export const createNewStore = async (newStore: NewStoreInfo) => {
    const { syncApiKey, syncApiSecret, hydrogenApiKey, hydrogenApiSecret, ...storeWithoutCredentials } = newStore;
    const updatedNewStore = Object.fromEntries(
        Object.entries(newStore).map(([key, value]) => [key, value === '' ? null : value])
    );
    const body = JSON.stringify(updatedNewStore);
    const response = await fetch(`${urlStore}/create`, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json'
        },
        body,
        credentials: 'include',
    });

    if (response.ok) {
        createLog({
            storeId: Number(newStore.id),
            entityType: 'store',
            action: 'create',
            newValue: storeWithoutCredentials,
        });
    }

    return response;
}

export const syncStoreWithFilters = async (filters: SyncFilters, storeID: number, time: boolean, email?: string) => {
    const options = [];

    if (filters.navigation)
        options.push("-n");
    if (filters.products)
        options.push("-p");
    if (filters.variants)
        options.push("-v");
    if (filters.brands)
        options.push("-b");
    if (filters.ranges)
        options.push("-r");
    if (filters.categories)
        options.push("-c");
    if (filters.collections)
        options.push("-o");
    if (filters.metafields)
        options.push("-z");
    if (filters.parser)
        options.push("-y");

    const uri = "&args=" + options.join("%20");
    const query = email;
    const response = await fetch(`${urlSync}/syncstore/${storeID}?email=${query}${uri}&time=${time}`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        credentials: 'include'
    });

    if (response.ok) {
        createLog({
            storeId: storeID,
            entityType: 'sync',
            section: 'manualSync',
            action: 'trigger',
            newValue: { filters, scheduled: time },
        });
    }

    return response;
}

export const getStoreJSONSettings = async (storeId: string) => {
    try {
        const response = await fetch(urlGetJSONSettings, {
            method: 'GET',
            headers: {
                'WS-API-KEY': `${process.env.REACT_APP_WS_API_KEY}`,
                'WS-STORE-ID': storeId,
            }
        });

        if (response.status === 204) {
            return { status: 204, data: null };
        }

        if (!response.ok) {
            throw new Error('Failed to fetch settings');
        }

        const data = await response.json();
        return { status: response.status, data };
    } catch (error) {
        throw new Error('Failed to fetch settings');
    }
};

export const putStoreJSONSettings = async (storeId: string, json: string) => {
    try {
        const body = json;
        const response = await fetch(urlPutJSONSettings, {
            method: 'POST',
            headers: {
                'WS-API-KEY': `${process.env.REACT_APP_WS_API_KEY}`,
                'WS-STORE-ID': storeId,
                'Content-Type': 'application/json'
            },
            body
        });

        if (!response.ok) {
            throw new Error('Failed to update settings');
        }

        return response;
    } catch (error) {
        throw new Error('Failed to update settings');
    }
};
